// A reducer behind a subscribe/getState pair, for state that many components
// read in small slices. A context holding the whole state rerenders every
// consumer on any change; a component that reads this store through
// useSyncExternalStore with a selector rerenders only when its own slice
// changes, and one that only dispatches rerenders never.
//
// Create one per provider instance (in a useState initializer), never at
// module level: the Next server renders every request in the same process, so
// a module-level store would be shared between visitors.
//
// Nothing here imports React, so it can be tested and used outside components.

export type ReducerStore<State, Action> = {
  getState: () => State;
  dispatch: (action: Action) => void;
  subscribe: (listener: () => void) => () => void;
};

export const createReducerStore = <State, Action>(
  reducer: (state: State, action: Action) => State,
  initialState: State
): ReducerStore<State, Action> => {
  let state = initialState;
  const listeners = new Set<() => void>();

  return {
    getState: () => state,
    dispatch: (action) => {
      const next = reducer(state, action);
      // A reducer that returns the state it was given changed nothing.
      if (Object.is(next, state)) return;
      state = next;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
};
